import { ScenarioWell, ScenarioWellCreate, ScenarioWellCreateWithName, ScenarioWellsBulkRequest, WellCreate, WellInfo, WellType } from '@/types/scenarioWell';
import { apiClient } from './client';

const BULK_PATCH_RETRIES = 5;
/** Backoff when CloudFront returns 403 "Request blocked". */
const CLOUDFRONT_RETRY_DELAYS_MS = [1000, 3000, 8000, 15000];

const isCloudFront403 = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const axiosResponse = (error as { response?: { status?: number; data?: unknown } }).response;
  if (axiosResponse?.status === 403) {
    const data = axiosResponse.data;
    if (typeof data === 'string') {
      const body = data.toLowerCase();
      if (body.includes('generated by cloudfront') || body.includes('request blocked')) {
        return true;
      }
    }
  }

  // apiClient transforms axios errors into { detail, status_code } — response body is lost.
  const apiError = error as { status_code?: number; detail?: string };
  if (apiError.status_code === 403) {
    const detail = (apiError.detail ?? '').toLowerCase();
    return (
      detail.includes('cloudfront') ||
      detail.includes('request blocked') ||
      detail.includes('status code 403')
    );
  }

  return false;
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const cloudFrontRetryDelayMs = (attempt: number): number =>
  CLOUDFRONT_RETRY_DELAYS_MS[Math.min(attempt - 1, CLOUDFRONT_RETRY_DELAYS_MS.length - 1)];

export const scenarioWellsApi = {
  // Get all wells for a scenario
  getScenarioWells: async (scenarioId: string): Promise<ScenarioWell[]> => {
    const response = await apiClient.get<ScenarioWell[]>(`/scenarios/${scenarioId}/wells`);
    return response;
  },

  // Bulk create/update/delete wells in a single transactional request.
  patchScenarioWellsBulk: async (scenarioId: string, body: ScenarioWellsBulkRequest): Promise<ScenarioWell[]> => {
    let lastError: unknown = null;
    for (let attempt = 1; attempt <= BULK_PATCH_RETRIES; attempt += 1) {
      try {
        return await apiClient.patch<ScenarioWell[]>(`/scenarios/${scenarioId}/wells/bulk`, body);
      } catch (error) {
        lastError = error;
        if (!isCloudFront403(error) || attempt === BULK_PATCH_RETRIES) {
          throw error;
        }
        await sleep(cloudFrontRetryDelayMs(attempt));
      }
    }
    throw lastError;
  },

  // Create a new well for a scenario (legacy, prefer patchScenarioWellsBulk)
  createScenarioWell: async (scenarioId: string, wellData: ScenarioWellCreateWithName): Promise<ScenarioWell> => {
    const response = await apiClient.post<ScenarioWell>(`/scenarios/${scenarioId}/wells`, wellData);
    return response;
  },

  // Update a well in a scenario (legacy, prefer patchScenarioWellsBulk)
  updateScenarioWell: async (scenarioWellId: string, wellData: ScenarioWellUpdate): Promise<ScenarioWell> => {
    const response = await apiClient.put<ScenarioWell>(`/scenario-wells/${scenarioWellId}`, wellData);
    return response;
  },

  // Delete a well from a scenario (legacy, prefer patchScenarioWellsBulk)
  deleteScenarioWell: async (scenarioWellId: string): Promise<void> => {
    await apiClient.delete(`/scenario-wells/${scenarioWellId}`);
  },
};

export const wellsApi = {
  // Get well types
  getWellTypes: async (): Promise<WellType[]> => {
    const response = await apiClient.get<WellType[]>('/wells/types/');
    return response;
  },

  // Create a new well
  createWell: async (wellData: WellCreate): Promise<WellInfo> => {
    const response = await apiClient.post<WellInfo>('/wells/', wellData);
    return response;
  },

  // Get all wells from user's organization
  getOrganizationWells: async (): Promise<import('@/api/fields').FieldLayerWellsGroup[]> => {
    const response = await apiClient.get<import('@/api/fields').FieldLayerWellsGroup[]>('/wells/');
    return response;
  },
};

// Convenience exports
export const { getScenarioWells, patchScenarioWellsBulk, createScenarioWell, updateScenarioWell, deleteScenarioWell } = scenarioWellsApi;
export const { getWellTypes, createWell, getOrganizationWells } = wellsApi;

// Type exports
export type { ScenarioWell, ScenarioWellCreate, ScenarioWellCreateWithName, ScenarioWellsBulkRequest, ScenarioWellBulkUpdateItem, WellCreate, WellInfo, WellType };
